import { useState, useMemo } from "react";
import { format, parseISO, startOfMonth, endOfMonth, eachDayOfInterval, startOfWeek, endOfWeek, isSameMonth, isSameDay, addMonths, subMonths, getDate, addDays, getDay } from "date-fns";
import { fr, enUS } from "date-fns/locale";
import { ChevronLeft, ChevronRight } from "lucide-react";
import { Card, CardContent } from "../client/components/ui/card";
import { CardSectionHeader, useIsDark } from "../client/components/ui/card-section-header";
import { Button } from "../client/components/ui/button";
import useTranslation from "../client/hooks/useTranslation";
import { useLanguageContext } from "../client/contexts/LanguageContext";
import { CURRENCY } from "../shared/currency";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "../client/components/ui/tooltip";
import { neonCellStyle, neonStrength } from "./calendarNeon";

export type CalendarEventType = "payout" | "expense" | "recurring" | "funded" | "reset";

export interface CalendarEvent {
  date: Date;
  type: CalendarEventType;
  amount: number;
  currency: string;
  label: string;
}

interface Props {
  events: CalendarEvent[];
  onMonthChange?: (month: Date) => void;
  className?: string;
}

function fmt(amount: number, currency: string) {
  return new Intl.NumberFormat(undefined, {
    style: "currency",
    currency,
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(amount);
}

const EVENT_DOT_COLORS: Record<CalendarEventType, string> = {
  payout: "bg-pnlPositive",
  expense: "bg-pnlNegative",
  recurring: "bg-orange-400",
  funded: "bg-teal-400",
  reset: "bg-violet-400",
};

const EVENT_BG_LIGHT: Record<CalendarEventType, string> = {
  payout: "bg-pnlPositive/10 border border-pnlPositive/40 border-l-2 border-l-pnlPositive",
  expense: "bg-pnlNegative/10 border border-pnlNegative/40 border-l-2 border-l-pnlNegative",
  recurring: "bg-orange-50 border border-orange-400/40 border-l-2 border-l-orange-400",
  funded: "bg-teal-50 border border-teal-400/40 border-l-2 border-l-teal-400",
  reset: "bg-violet-50 border border-violet-400/40 border-l-2 border-l-violet-400",
};

const EVENT_TEXT_COLORS: Record<CalendarEventType, string> = {
  payout: "text-pnlPositive",
  expense: "text-pnlNegative",
  recurring: "text-orange-400",
  funded: "text-teal-400",
  reset: "text-violet-400",
};

export default function PropFirmCalendar({ events, onMonthChange, className }: Props) {
  const t = useTranslation();
  const { language } = useLanguageContext();
  const isDark = useIsDark();
  const [currentMonth, setCurrentMonth] = useState(() => startOfMonth(new Date()));

  const locale = language === "fr" ? fr : enUS;

  const calendarDays = useMemo(() => {
    const monthStart = startOfMonth(currentMonth);
    const monthEnd = endOfMonth(currentMonth);
    const calStart = startOfWeek(monthStart, { weekStartsOn: 1 });
    const calEnd = endOfWeek(monthEnd, { weekStartsOn: 1 });
    return eachDayOfInterval({ start: calStart, end: calEnd });
  }, [currentMonth]);

  const eventsByDay = useMemo(() => {
    const map = new Map<string, CalendarEvent[]>();
    for (const event of events) {
      const key = format(new Date(event.date), "yyyy-MM-dd");
      if (!map.has(key)) map.set(key, []);
      map.get(key)!.push(event);
    }
    return map;
  }, [events]);

  // Plus gros mouvement net d'un jour du mois affiché : étalon de la lueur
  // des cases (thèmes sombres), comme dans le calendrier Lite.
  const monthMax = useMemo(() => {
    let max = 0;
    for (const [key, dayEvents] of eventsByDay) {
      if (!isSameMonth(parseISO(key), currentMonth)) continue;
      const net = dayEvents.reduce((s, e) => s + (e.type === "payout" ? e.amount : e.type === "expense" || e.type === "recurring" ? -e.amount : 0), 0);
      max = Math.max(max, Math.abs(net));
    }
    return max;
  }, [eventsByDay, currentMonth]);

  const weeks = useMemo(() => {
    const result: Date[][] = [];
    for (let i = 0; i < calendarDays.length; i += 7) {
      result.push(calendarDays.slice(i, i + 7));
    }
    return result;
  }, [calendarDays]);

  const weekDays = useMemo(() => {
    const start = startOfWeek(new Date(), { weekStartsOn: 1 });
    return Array.from({ length: 7 }, (_, i) =>
      format(addDays(start, i), "EEE", { locale })
    );
  }, [locale]);

  const previousMonth = () => {
    const next = subMonths(currentMonth, 1);
    setCurrentMonth(next);
    onMonthChange?.(next);
  };

  const nextMonth = () => {
    const next = addMonths(currentMonth, 1);
    setCurrentMonth(next);
    onMonthChange?.(next);
  };

  // Dominant type for a day (reset > payout > funded > expense > recurring).
  // Le reset passe en tête : c'est un événement rare et structurant (le compte
  // repart de zéro, tout l'historique de la phase précédente est invalidé).
  // Il éclipse volontairement le payout, car un jour qui porte les deux raconte
  // avant tout la fin d'un cycle. Les jalons plus fréquents (payout, dépenses)
  // restent visibles via les pastilles et le tooltip.
  function dominantType(dayEvents: CalendarEvent[]): CalendarEventType | null {
    if (dayEvents.length === 0) return null;
    if (dayEvents.some(e => e.type === "reset")) return "reset";
    if (dayEvents.some(e => e.type === "payout")) return "payout";
    if (dayEvents.some(e => e.type === "funded")) return "funded";
    if (dayEvents.some(e => e.type === "expense")) return "expense";
    return "recurring";
  }

  return (
    <Card className={className}>
      <CardSectionHeader
        label={format(currentMonth, "MMMM yyyy", { locale })}
        isDark={isDark}
        rightContent={
          <div className="flex items-center gap-2">
            <Button variant="outline" size="sm" onClick={previousMonth}>
              <ChevronLeft className="h-4 w-4" />
            </Button>
            <Button variant="outline" size="sm" onClick={nextMonth}>
              <ChevronRight className="h-4 w-4" />
            </Button>
          </div>
        }
      />
      <CardContent className="px-5 pb-5 pt-1">
        {/* Week day headers */}
        <div className="grid grid-cols-7 gap-1 mb-2">
          {weekDays.map((d, i) => (
            <div key={i} className="text-center text-xs font-semibold text-muted-foreground py-1">
              <span className="hidden sm:inline">{d}</span>
              <span className="sm:hidden">{d.charAt(0)}</span>
            </div>
          ))}
        </div>

        {/* Calendar grid */}
        <TooltipProvider delayDuration={200}>
        <div className="flex flex-col gap-1">
          {weeks.map((week, wi) => (
            <div key={wi} className="grid grid-cols-7 gap-1">
              {week.map((day) => {
                const key = format(day, "yyyy-MM-dd");
                const dayEvents = eventsByDay.get(key) ?? [];
                const isCurrentMonth = isSameMonth(day, currentMonth);
                const isToday = isSameDay(day, new Date());
                const dominant = dominantType(dayEvents);
                const isWeekend = getDay(day) === 0 || getDay(day) === 6;

                // Thèmes sombres : case « enseigne » (dégradé + liseré + halo) en
                // style inline. Thèmes clairs : classes plates historiques.
                const neon = isDark && !!dominant && isCurrentMonth;
                let bgClass = isCurrentMonth ? "bg-card border border-border/50" : "bg-muted/20 border border-border/25";
                if (dominant && isCurrentMonth) {
                  bgClass = neon ? "border border-transparent hover:brightness-125" : EVENT_BG_LIGHT[dominant];
                }

                const grouped = {
                  payout: dayEvents.filter(e => e.type === "payout"),
                  expense: dayEvents.filter(e => e.type === "expense"),
                  recurring: dayEvents.filter(e => e.type === "recurring"),
                  funded: dayEvents.filter(e => e.type === "funded"),
                  reset: dayEvents.filter(e => e.type === "reset"),
                };
                const totalPayout = grouped.payout.reduce((s, e) => s + e.amount, 0);
                const totalExpense = [...grouped.expense, ...grouped.recurring].reduce((s, e) => s + e.amount, 0);
                const neonStyle = neon && dominant
                  ? neonCellStyle(dominant, neonStrength(totalPayout - totalExpense, monthMax), { today: isToday })
                  : undefined;

                const cellDiv = (
                  <div
                    style={neonStyle}
                    className={`min-h-[56px] sm:min-h-[80px] rounded-md p-1 sm:p-1.5 flex flex-col transition-[filter] ${bgClass} ${
                      isToday && !neon ? "ring-2 ring-primary ring-offset-1 ring-offset-background" : ""
                    } ${!isCurrentMonth ? "opacity-50" : ""}`}
                  >
                    <div className="flex items-center justify-between mb-1">
                      <span className={`text-xs font-semibold leading-none ${
                        !isCurrentMonth ? "text-muted-foreground/40"
                        : isToday ? "text-primary"
                        : isWeekend ? "text-muted-foreground/60"
                        : "text-foreground/70"
                      }`}>
                        {getDate(day)}
                      </span>
                      {isCurrentMonth && dayEvents.length > 0 && (
                        <div className="flex items-center gap-0.5">
                          {Array.from(new Set(dayEvents.map(e => e.type))).map(type => (
                            <div key={type} className={`w-1.5 h-1.5 rounded-full ${EVENT_DOT_COLORS[type]}`} />
                          ))}
                        </div>
                      )}
                    </div>

                    {isCurrentMonth && dayEvents.length > 0 ? (
                      <div className="flex flex-col gap-0.5 mt-auto min-w-0">
                        {grouped.funded.length > 0 && (
                          <span className="text-[11px] leading-none">🏆</span>
                        )}
                        {grouped.reset.length > 0 && (
                          <span className="text-[10px] sm:text-xs font-bold leading-tight truncate text-violet-400">
                            🔄 {t.propFirmManager.calendarReset}
                          </span>
                        )}
                        {totalPayout > 0 && (
                          <span className="text-[10px] sm:text-sm font-bold leading-tight truncate text-pnlPositive">
                            💰 +{fmt(totalPayout, CURRENCY)}
                          </span>
                        )}
                        {totalExpense > 0 && (
                          <span className={`text-[10px] sm:text-xs font-bold leading-tight truncate ${dominant === "expense" ? "text-pnlNegative" : "text-orange-400"}`}>
                            -{fmt(totalExpense, CURRENCY)}
                          </span>
                        )}
                        <span className="hidden sm:block text-[10px] text-muted-foreground leading-none">
                          {dayEvents.length} {dayEvents.length > 1 ? t.propFirmManager.calendarEventPlural : t.propFirmManager.calendarEventSingular}
                        </span>
                      </div>
                    ) : isCurrentMonth ? (
                      <div className="mt-auto">
                        <span className="hidden sm:block text-[10px] text-muted-foreground/40">-</span>
                      </div>
                    ) : null}
                  </div>
                );

                if (!isCurrentMonth || dayEvents.length === 0) {
                  return <div key={key}>{cellDiv}</div>;
                }

                return (
                  <Tooltip key={key}>
                    <TooltipTrigger asChild>
                      {cellDiv}
                    </TooltipTrigger>
                    <TooltipContent side="top" sideOffset={4} className="p-0 max-w-[300px]">
                      <div className="px-3 py-2 flex flex-col gap-2 text-xs">
                        {grouped.payout.length > 0 && (
                          <div className="flex flex-col gap-1">
                            <span className="font-semibold text-pnlPositive">{t.propFirmManager.calendarTooltipPayouts}</span>
                            {grouped.payout.map((e, i) => (
                              <div key={i} className="flex items-center justify-between gap-3">
                                <span className="text-foreground/80">{e.label}</span>
                                <span className="font-mono text-pnlPositive shrink-0">+{fmt(e.amount, e.currency)}</span>
                              </div>
                            ))}
                          </div>
                        )}
                        {grouped.expense.length > 0 && (
                          <div className="flex flex-col gap-1">
                            <span className="font-semibold text-pnlNegative">{t.propFirmManager.calendarTooltipExpenses}</span>
                            {grouped.expense.map((e, i) => (
                              <div key={i} className="flex items-center justify-between gap-3">
                                <span className="text-foreground/80">{e.label}</span>
                                <span className="font-mono text-pnlNegative shrink-0">-{fmt(e.amount, e.currency)}</span>
                              </div>
                            ))}
                          </div>
                        )}
                        {grouped.recurring.length > 0 && (
                          <div className="flex flex-col gap-1">
                            <span className="font-semibold text-orange-400">{t.propFirmManager.calendarTooltipRecurring}</span>
                            {grouped.recurring.map((e, i) => (
                              <div key={i} className="flex items-center justify-between gap-3">
                                <span className="text-foreground/80">{e.label}</span>
                                <span className="font-mono text-orange-400 shrink-0">-{fmt(e.amount, e.currency)}</span>
                              </div>
                            ))}
                          </div>
                        )}
                        {grouped.funded.length > 0 && (
                          <div className="flex flex-col gap-1">
                            <span className="font-semibold text-teal-400">{t.propFirmManager.calendarTooltipFunded}</span>
                            {grouped.funded.map((e, i) => (
                              <div key={i} className="flex items-center gap-2">
                                <span className="text-foreground/80">🏆 {e.label}</span>
                              </div>
                            ))}
                          </div>
                        )}
                        {grouped.reset.length > 0 && (
                          <div className="flex flex-col gap-1">
                            <span className="font-semibold text-violet-400">{t.propFirmManager.calendarTooltipReset}</span>
                            {grouped.reset.map((e, i) => (
                              <div key={i} className="flex items-center gap-2">
                                <span className="text-foreground/80">🔄 {e.label}</span>
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                    </TooltipContent>
                  </Tooltip>
                );
              })}
            </div>
          ))}
        </div>
        </TooltipProvider>

        {/* Legend */}
        <div className="mt-4 flex flex-wrap gap-x-5 gap-y-2 text-xs text-muted-foreground">
          <div className="flex items-center gap-1.5">
            <div className={`w-3 h-3 rounded-sm ${isDark ? "" : EVENT_BG_LIGHT.payout}`} style={isDark ? neonCellStyle("payout", 1) : undefined} />
            <span>{t.propFirmManager.calendarPayout}</span>
          </div>
          <div className="flex items-center gap-1.5">
            <div className={`w-3 h-3 rounded-sm ${isDark ? "" : EVENT_BG_LIGHT.expense}`} style={isDark ? neonCellStyle("expense", 1) : undefined} />
            <span>{t.propFirmManager.calendarExpense}</span>
          </div>
          <div className="flex items-center gap-1.5">
            <div className={`w-3 h-3 rounded-sm ${isDark ? "" : EVENT_BG_LIGHT.recurring}`} style={isDark ? neonCellStyle("recurring", 1) : undefined} />
            <span>{t.propFirmManager.calendarRecurring}</span>
          </div>
          <div className="flex items-center gap-1.5">
            <span className="text-sm">🏆</span>
            <span>{t.propFirmManager.calendarFunded}</span>
          </div>
          <div className="flex items-center gap-1.5">
            <span className="text-sm">🔄</span>
            <span>{t.propFirmManager.calendarReset}</span>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}
